import type {
  DocumentFieldDefinition,
  DocumentSectionDefinition,
} from '@/features/case-workspace/types'

const DATE_RULE = {
  type: 'pattern' as const,
  value: '^(0[1-9]|1[0-2])\\/(0[1-9]|[12][0-9]|3[01])\\/\\d{4}$',
  message: 'Use MM/DD/YYYY format.',
}

const ORIGIN_EXPORT_COUNTRY_RULE = {
  type: 'pattern' as const,
  value: '^([A-Z]{2}|MULTI)$',
  message: 'Use a two letter ISO country code or MULTI where the instructions allow it.',
}

const METAL_COUNTRY_RULE = {
  type: 'pattern' as const,
  value: '^([A-Z]{2}|OTH|N\\/A)$',
  message: 'Use a two letter ISO country code, OTH, or N/A as required by the instructions.',
}

const PORT_RULE = {
  type: 'pattern' as const,
  value: '^\\d{4}$',
  message: 'Use the four digit U.S. port code format.',
}

const IDENTIFIER_RULE = {
  type: 'pattern' as const,
  value: '^(SAME|00-0000000|\\d{2}-\\d{7}([A-Z0-9]{2})?|\\d{3}-\\d{2}-\\d{4}|\\d{6}-\\d{5})$',
  message: 'Use EIN, EIN with suffix, SSN, CBP assigned number, SAME, or 00-0000000 for consolidated shipments.',
}

const CURRENCY_RULE = {
  type: 'pattern' as const,
  value: '^\\d+(\\.\\d{1,2})?$',
  message: 'Use a whole dollar or currency amount with up to two decimals.',
}

const NUMERIC_RULE = {
  type: 'pattern' as const,
  value: '^\\d+(\\.\\d+)?$',
  message: 'Use a numeric value.',
}

const ZIP_CODE_RULE = {
  type: 'pattern' as const,
  value: '^\\d{5}(-\\d{4})?$',
  message: 'Use a five digit ZIP code or ZIP+4 format.',
}

const YES_NO_OPTIONS = [
  { value: 'Y', label: 'Y — Related' },
  { value: 'N', label: 'N — Not related' },
]

export const CBP_7501_ENTRY_TYPE_OPTIONS = [
  {
    label: 'Most Common',
    options: [
      { value: '01', label: '01 — Consumption, free and dutiable' },
      { value: '03', label: '03 — Consumption, AD/CVD' },
      { value: '06', label: '06 — Foreign Trade Zone consumption' },
      { value: '11', label: '11 — Informal, free and dutiable' },
    ],
  },
  {
    label: 'Least Common',
    options: [
      { value: '02', label: '02 — Consumption, quota/visa' },
      { value: '04', label: '04 — Consumption, appraisement' },
      { value: '05', label: '05 — Consumption, vessel repair' },
      { value: '07', label: '07 — Quota/visa and AD/CVD combination' },
      { value: '08', label: '08 — Duty deferral' },
      { value: '12', label: '12 — Informal, quota other than textiles' },
      { value: '22', label: '22 — Re-warehouse' },
      { value: '24', label: '24 — Trade fair' },
      { value: '25', label: '25 — Permanent exhibition' },
      { value: '31', label: '31 — Warehouse withdrawal for consumption' },
      { value: '32', label: '32 — Warehouse withdrawal, quota/visa' },
      { value: '34', label: '34 — Warehouse withdrawal, AD/CVD' },
      { value: '38', label: '38 — Warehouse withdrawal, quota/visa and AD/CVD combination' },
      { value: '51', label: '51 — Defense Contract Management Command, military only' },
      { value: '52', label: '52 — U.S. federal government agency' },
    ],
  },
  {
    label: 'Unique Entry Types',
    options: [
      { value: '21', label: '21 — Warehouse' },
      { value: '23', label: '23 — Temporary importation bond' },
      { value: '26', label: '26 — Foreign Trade Zone admission' },
      { value: '61', label: '61 — Immediate transportation' },
      { value: '62', label: '62 — Transportation and exportation' },
      { value: '63', label: '63 — Immediate exportation' },
    ],
  },
]

export const CBP_7501_BOND_TYPE_OPTIONS = [
  { value: '0', label: '0 — Government or no bond required' },
  { value: '8', label: '8 — Continuous' },
  { value: '9', label: '9 — Single transaction' },
]

export const CBP_7501_MODE_OF_TRANSPORT_OPTIONS = [
  { value: '10', label: '10 — Vessel, non-container' },
  { value: '11', label: '11 — Vessel, container' },
  { value: '12', label: '12 — Border, waterborne' },
  { value: '20', label: '20 — Rail, non-container' },
  { value: '21', label: '21 — Rail, container' },
  { value: '30', label: '30 — Truck, non-container' },
  { value: '31', label: '31 — Truck, container' },
  { value: '32', label: '32 — Auto' },
  { value: '33', label: '33 — Pedestrian' },
  { value: '34', label: '34 — Road, other' },
  { value: '40', label: '40 — Air, non-container' },
  { value: '41', label: '41 — Air, container' },
  { value: '50', label: '50 — Mail' },
  { value: '60', label: '60 — Passenger, hand-carried' },
  { value: '70', label: '70 — Fixed transport installation' },
]

export const CBP_7501_MISSING_DOC_CODE_OPTIONS = [
  { value: '01', label: '01 — Commercial invoice' },
  { value: '10', label: '10 — CBP Form 5523' },
  { value: '14', label: '14 — Other invoice' },
  { value: '15', label: '15 — Declaration statement' },
  { value: '16', label: '16 — Corrected commercial invoice' },
  { value: '17', label: '17 — Other agency form' },
  { value: '18', label: '18 — Missing shipment data' },
  { value: '19', label: '19 — Scale weight' },
  { value: '20', label: '20 — Additional customs support' },
  { value: '21', label: '21 — Coffee Form O' },
  { value: '22', label: '22 — Chemical analysis' },
  { value: '23', label: '23 — Outturn report' },
  { value: '26', label: '26 — Packing list' },
  { value: '98', label: '98 — Not specified above' },
  { value: '99', label: '99 — Three or more missing documents' },
]

export const CBP_7501_WEIGHT_UNIT_OPTIONS = [
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'lbs', label: 'lbs' },
  { value: 'oz', label: 'oz' },
  { value: 'tons', label: 'tons' },
]

export const CBP_7501_MANIFEST_QUANTITY_UNIT_OPTIONS = [
  { value: 'ea', label: 'ea' },
  { value: 'pcs', label: 'pcs' },
  { value: 'pkg', label: 'pkg' },
  { value: 'ctn', label: 'ctn' },
  { value: 'pal', label: 'pal' },
  { value: 'doz', label: 'doz' },
]

const CBP_7501_BASE_COUNTRY_CODE_OPTIONS = [
  { value: 'AF', label: 'AF — Afghanistan' },
  { value: 'AX', label: 'AX — Åland Islands' },
  { value: 'AL', label: 'AL — Albania' },
  { value: 'DZ', label: 'DZ — Algeria' },
  { value: 'AS', label: 'AS — American Samoa' },
  { value: 'AD', label: 'AD — Andorra' },
  { value: 'AO', label: 'AO — Angola' },
  { value: 'AI', label: 'AI — Anguilla' },
  { value: 'AQ', label: 'AQ — Antarctica' },
  { value: 'AG', label: 'AG — Antigua & Barbuda' },
  { value: 'AR', label: 'AR — Argentina' },
  { value: 'AM', label: 'AM — Armenia' },
  { value: 'AW', label: 'AW — Aruba' },
  { value: 'AU', label: 'AU — Australia' },
  { value: 'AT', label: 'AT — Austria' },
  { value: 'AZ', label: 'AZ — Azerbaijan' },
  { value: 'BS', label: 'BS — Bahamas' },
  { value: 'BH', label: 'BH — Bahrain' },
  { value: 'BD', label: 'BD — Bangladesh' },
  { value: 'BB', label: 'BB — Barbados' },
  { value: 'BY', label: 'BY — Belarus' },
  { value: 'BE', label: 'BE — Belgium' },
  { value: 'BZ', label: 'BZ — Belize' },
  { value: 'BJ', label: 'BJ — Benin' },
  { value: 'BM', label: 'BM — Bermuda' },
  { value: 'BT', label: 'BT — Bhutan' },
  { value: 'BO', label: 'BO — Bolivia' },
  { value: 'BQ', label: 'BQ — Caribbean Netherlands' },
  { value: 'BA', label: 'BA — Bosnia & Herzegovina' },
  { value: 'BW', label: 'BW — Botswana' },
  { value: 'BV', label: 'BV — Bouvet Island' },
  { value: 'BR', label: 'BR — Brazil' },
  { value: 'IO', label: 'IO — British Indian Ocean Territory' },
  { value: 'BN', label: 'BN — Brunei' },
  { value: 'BG', label: 'BG — Bulgaria' },
  { value: 'BF', label: 'BF — Burkina Faso' },
  { value: 'BI', label: 'BI — Burundi' },
  { value: 'CV', label: 'CV — Cape Verde' },
  { value: 'KH', label: 'KH — Cambodia' },
  { value: 'CM', label: 'CM — Cameroon' },
  { value: 'CA', label: 'CA — Canada' },
  { value: 'KY', label: 'KY — Cayman Islands' },
  { value: 'CF', label: 'CF — Central African Republic' },
  { value: 'TD', label: 'TD — Chad' },
  { value: 'CL', label: 'CL — Chile' },
  { value: 'CN', label: 'CN — China' },
  { value: 'CX', label: 'CX — Christmas Island' },
  { value: 'CC', label: 'CC — Cocos (Keeling) Islands' },
  { value: 'CO', label: 'CO — Colombia' },
  { value: 'KM', label: 'KM — Comoros' },
  { value: 'CG', label: 'CG — Congo - Brazzaville' },
  { value: 'CD', label: 'CD — Congo - Kinshasa' },
  { value: 'CK', label: 'CK — Cook Islands' },
  { value: 'CR', label: 'CR — Costa Rica' },
  { value: 'CI', label: 'CI — Côte d’Ivoire' },
  { value: 'HR', label: 'HR — Croatia' },
  { value: 'CU', label: 'CU — Cuba' },
  { value: 'CW', label: 'CW — Curaçao' },
  { value: 'CY', label: 'CY — Cyprus' },
  { value: 'CZ', label: 'CZ — Czechia' },
  { value: 'DK', label: 'DK — Denmark' },
  { value: 'DJ', label: 'DJ — Djibouti' },
  { value: 'DM', label: 'DM — Dominica' },
  { value: 'DO', label: 'DO — Dominican Republic' },
  { value: 'EC', label: 'EC — Ecuador' },
  { value: 'EG', label: 'EG — Egypt' },
  { value: 'SV', label: 'SV — El Salvador' },
  { value: 'GQ', label: 'GQ — Equatorial Guinea' },
  { value: 'ER', label: 'ER — Eritrea' },
  { value: 'EE', label: 'EE — Estonia' },
  { value: 'SZ', label: 'SZ — Eswatini' },
  { value: 'ET', label: 'ET — Ethiopia' },
  { value: 'FK', label: 'FK — Falkland Islands' },
  { value: 'FO', label: 'FO — Faroe Islands' },
  { value: 'FJ', label: 'FJ — Fiji' },
  { value: 'FI', label: 'FI — Finland' },
  { value: 'FR', label: 'FR — France' },
  { value: 'GF', label: 'GF — French Guiana' },
  { value: 'PF', label: 'PF — French Polynesia' },
  { value: 'TF', label: 'TF — French Southern Territories' },
  { value: 'GA', label: 'GA — Gabon' },
  { value: 'GM', label: 'GM — Gambia' },
  { value: 'GE', label: 'GE — Georgia' },
  { value: 'DE', label: 'DE — Germany' },
  { value: 'GH', label: 'GH — Ghana' },
  { value: 'GI', label: 'GI — Gibraltar' },
  { value: 'GR', label: 'GR — Greece' },
  { value: 'GL', label: 'GL — Greenland' },
  { value: 'GD', label: 'GD — Grenada' },
  { value: 'GP', label: 'GP — Guadeloupe' },
  { value: 'GU', label: 'GU — Guam' },
  { value: 'GT', label: 'GT — Guatemala' },
  { value: 'GG', label: 'GG — Guernsey' },
  { value: 'GN', label: 'GN — Guinea' },
  { value: 'GW', label: 'GW — Guinea-Bissau' },
  { value: 'GY', label: 'GY — Guyana' },
  { value: 'HT', label: 'HT — Haiti' },
  { value: 'HM', label: 'HM — Heard & McDonald Islands' },
  { value: 'VA', label: 'VA — Vatican City' },
  { value: 'HN', label: 'HN — Honduras' },
  { value: 'HK', label: 'HK — Hong Kong SAR China' },
  { value: 'HU', label: 'HU — Hungary' },
  { value: 'IS', label: 'IS — Iceland' },
  { value: 'IN', label: 'IN — India' },
  { value: 'ID', label: 'ID — Indonesia' },
  { value: 'IR', label: 'IR — Iran' },
  { value: 'IQ', label: 'IQ — Iraq' },
  { value: 'IE', label: 'IE — Ireland' },
  { value: 'IM', label: 'IM — Isle of Man' },
  { value: 'IL', label: 'IL — Israel' },
  { value: 'IT', label: 'IT — Italy' },
  { value: 'JM', label: 'JM — Jamaica' },
  { value: 'JP', label: 'JP — Japan' },
  { value: 'JE', label: 'JE — Jersey' },
  { value: 'JO', label: 'JO — Jordan' },
  { value: 'KZ', label: 'KZ — Kazakhstan' },
  { value: 'KE', label: 'KE — Kenya' },
  { value: 'KI', label: 'KI — Kiribati' },
  { value: 'KP', label: 'KP — North Korea' },
  { value: 'KR', label: 'KR — South Korea' },
  { value: 'KW', label: 'KW — Kuwait' },
  { value: 'KG', label: 'KG — Kyrgyzstan' },
  { value: 'LA', label: 'LA — Laos' },
  { value: 'LV', label: 'LV — Latvia' },
  { value: 'LB', label: 'LB — Lebanon' },
  { value: 'LS', label: 'LS — Lesotho' },
  { value: 'LR', label: 'LR — Liberia' },
  { value: 'LY', label: 'LY — Libya' },
  { value: 'LI', label: 'LI — Liechtenstein' },
  { value: 'LT', label: 'LT — Lithuania' },
  { value: 'LU', label: 'LU — Luxembourg' },
  { value: 'MO', label: 'MO — Macao SAR China' },
  { value: 'MG', label: 'MG — Madagascar' },
  { value: 'MW', label: 'MW — Malawi' },
  { value: 'MY', label: 'MY — Malaysia' },
  { value: 'MV', label: 'MV — Maldives' },
  { value: 'ML', label: 'ML — Mali' },
  { value: 'MT', label: 'MT — Malta' },
  { value: 'MH', label: 'MH — Marshall Islands' },
  { value: 'MQ', label: 'MQ — Martinique' },
  { value: 'MR', label: 'MR — Mauritania' },
  { value: 'MU', label: 'MU — Mauritius' },
  { value: 'YT', label: 'YT — Mayotte' },
  { value: 'MX', label: 'MX — Mexico' },
  { value: 'FM', label: 'FM — Micronesia' },
  { value: 'MD', label: 'MD — Moldova' },
  { value: 'MC', label: 'MC — Monaco' },
  { value: 'MN', label: 'MN — Mongolia' },
  { value: 'ME', label: 'ME — Montenegro' },
  { value: 'MS', label: 'MS — Montserrat' },
  { value: 'MA', label: 'MA — Morocco' },
  { value: 'MZ', label: 'MZ — Mozambique' },
  { value: 'MM', label: 'MM — Myanmar (Burma)' },
  { value: 'NA', label: 'NA — Namibia' },
  { value: 'NR', label: 'NR — Nauru' },
  { value: 'NP', label: 'NP — Nepal' },
  { value: 'NL', label: 'NL — Netherlands' },
  { value: 'NC', label: 'NC — New Caledonia' },
  { value: 'NZ', label: 'NZ — New Zealand' },
  { value: 'NI', label: 'NI — Nicaragua' },
  { value: 'NE', label: 'NE — Niger' },
  { value: 'NG', label: 'NG — Nigeria' },
  { value: 'NU', label: 'NU — Niue' },
  { value: 'NF', label: 'NF — Norfolk Island' },
  { value: 'MK', label: 'MK — North Macedonia' },
  { value: 'MP', label: 'MP — Northern Mariana Islands' },
  { value: 'NO', label: 'NO — Norway' },
  { value: 'OM', label: 'OM — Oman' },
  { value: 'PK', label: 'PK — Pakistan' },
  { value: 'PW', label: 'PW — Palau' },
  { value: 'PS', label: 'PS — Palestinian Territories' },
  { value: 'PA', label: 'PA — Panama' },
  { value: 'PG', label: 'PG — Papua New Guinea' },
  { value: 'PY', label: 'PY — Paraguay' },
  { value: 'PE', label: 'PE — Peru' },
  { value: 'PH', label: 'PH — Philippines' },
  { value: 'PN', label: 'PN — Pitcairn Islands' },
  { value: 'PL', label: 'PL — Poland' },
  { value: 'PT', label: 'PT — Portugal' },
  { value: 'PR', label: 'PR — Puerto Rico' },
  { value: 'QA', label: 'QA — Qatar' },
  { value: 'RE', label: 'RE — Réunion' },
  { value: 'RO', label: 'RO — Romania' },
  { value: 'RU', label: 'RU — Russia' },
  { value: 'RW', label: 'RW — Rwanda' },
  { value: 'BL', label: 'BL — St. Barthélemy' },
  { value: 'SH', label: 'SH — St. Helena' },
  { value: 'KN', label: 'KN — St. Kitts & Nevis' },
  { value: 'LC', label: 'LC — St. Lucia' },
  { value: 'MF', label: 'MF — St. Martin' },
  { value: 'PM', label: 'PM — St. Pierre & Miquelon' },
  { value: 'VC', label: 'VC — St. Vincent & Grenadines' },
  { value: 'WS', label: 'WS — Samoa' },
  { value: 'SM', label: 'SM — San Marino' },
  { value: 'ST', label: 'ST — São Tomé & Príncipe' },
  { value: 'SA', label: 'SA — Saudi Arabia' },
  { value: 'SN', label: 'SN — Senegal' },
  { value: 'RS', label: 'RS — Serbia' },
  { value: 'SC', label: 'SC — Seychelles' },
  { value: 'SL', label: 'SL — Sierra Leone' },
  { value: 'SG', label: 'SG — Singapore' },
  { value: 'SX', label: 'SX — Sint Maarten' },
  { value: 'SK', label: 'SK — Slovakia' },
  { value: 'SI', label: 'SI — Slovenia' },
  { value: 'SB', label: 'SB — Solomon Islands' },
  { value: 'SO', label: 'SO — Somalia' },
  { value: 'ZA', label: 'ZA — South Africa' },
  { value: 'GS', label: 'GS — South Georgia & South Sandwich Islands' },
  { value: 'SS', label: 'SS — South Sudan' },
  { value: 'ES', label: 'ES — Spain' },
  { value: 'LK', label: 'LK — Sri Lanka' },
  { value: 'SD', label: 'SD — Sudan' },
  { value: 'SR', label: 'SR — Suriname' },
  { value: 'SJ', label: 'SJ — Svalbard & Jan Mayen' },
  { value: 'SE', label: 'SE — Sweden' },
  { value: 'CH', label: 'CH — Switzerland' },
  { value: 'SY', label: 'SY — Syria' },
  { value: 'TW', label: 'TW — Taiwan' },
  { value: 'TJ', label: 'TJ — Tajikistan' },
  { value: 'TZ', label: 'TZ — Tanzania' },
  { value: 'TH', label: 'TH — Thailand' },
  { value: 'TL', label: 'TL — Timor-Leste' },
  { value: 'TG', label: 'TG — Togo' },
  { value: 'TK', label: 'TK — Tokelau' },
  { value: 'TO', label: 'TO — Tonga' },
  { value: 'TT', label: 'TT — Trinidad & Tobago' },
  { value: 'TN', label: 'TN — Tunisia' },
  { value: 'TR', label: 'TR — Türkiye' },
  { value: 'TM', label: 'TM — Turkmenistan' },
  { value: 'TC', label: 'TC — Turks & Caicos Islands' },
  { value: 'TV', label: 'TV — Tuvalu' },
  { value: 'UG', label: 'UG — Uganda' },
  { value: 'UA', label: 'UA — Ukraine' },
  { value: 'AE', label: 'AE — United Arab Emirates' },
  { value: 'GB', label: 'GB — United Kingdom' },
  { value: 'UM', label: 'UM — U.S. Outlying Islands' },
  { value: 'US', label: 'US — United States' },
  { value: 'UY', label: 'UY — Uruguay' },
  { value: 'UZ', label: 'UZ — Uzbekistan' },
  { value: 'VU', label: 'VU — Vanuatu' },
  { value: 'VE', label: 'VE — Venezuela' },
  { value: 'VN', label: 'VN — Vietnam' },
  { value: 'VG', label: 'VG — British Virgin Islands' },
  { value: 'VI', label: 'VI — U.S. Virgin Islands' },
  { value: 'WF', label: 'WF — Wallis & Futuna' },
  { value: 'EH', label: 'EH — Western Sahara' },
  { value: 'YE', label: 'YE — Yemen' },
  { value: 'ZM', label: 'ZM — Zambia' },
  { value: 'ZW', label: 'ZW — Zimbabwe' },
]

const INSIDE_US_COUNTRY_CODES = ['US', 'VI', 'PR', 'GU', 'AS', 'MP', 'UM'] as const

const INSIDE_US_COUNTRY_OPTIONS = INSIDE_US_COUNTRY_CODES
  .map((countryCode) => CBP_7501_BASE_COUNTRY_CODE_OPTIONS.find((option) => option.value === countryCode))
  .filter((option): option is NonNullable<typeof option> => Boolean(option))

const REMAINING_COUNTRY_OPTIONS = CBP_7501_BASE_COUNTRY_CODE_OPTIONS.filter(
  (option) => !INSIDE_US_COUNTRY_CODES.includes(option.value as (typeof INSIDE_US_COUNTRY_CODES)[number]),
)

export const CBP_7501_COUNTRY_CODE_OPTIONS = [
  {
    label: 'Inside the US',
    options: INSIDE_US_COUNTRY_OPTIONS,
  },
  {
    label: 'All Countries',
    options: [
      { value: 'MULTI', label: 'MULTI — Multiple countries (line-level detail required)' },
      ...REMAINING_COUNTRY_OPTIONS,
    ],
  },
]

export const CBP_7501_ADDRESS_COUNTRY_OPTIONS = [
  {
    label: 'Inside the US',
    options: INSIDE_US_COUNTRY_OPTIONS,
  },
  {
    label: 'All Countries',
    options: REMAINING_COUNTRY_OPTIONS,
  },
]

export const CBP_7501_STATE_ISLAND_OPTIONS = [
  {
    label: 'USVI & Territories',
    options: [
      { value: 'VI', label: 'VI — U.S. Virgin Islands' },
      { value: 'PR', label: 'PR — Puerto Rico' },
      { value: 'GU', label: 'GU — Guam' },
      { value: 'AS', label: 'AS — American Samoa' },
      { value: 'MP', label: 'MP — Northern Mariana Islands' },
      { value: 'DC', label: 'DC — District of Columbia' },
    ],
  },
  {
    label: 'U.S. States',
    options: [
      { value: 'AL', label: 'AL — Alabama' },
      { value: 'AK', label: 'AK — Alaska' },
      { value: 'AZ', label: 'AZ — Arizona' },
      { value: 'AR', label: 'AR — Arkansas' },
      { value: 'CA', label: 'CA — California' },
      { value: 'CO', label: 'CO — Colorado' },
      { value: 'CT', label: 'CT — Connecticut' },
      { value: 'DE', label: 'DE — Delaware' },
      { value: 'FL', label: 'FL — Florida' },
      { value: 'GA', label: 'GA — Georgia' },
      { value: 'HI', label: 'HI — Hawaii' },
      { value: 'ID', label: 'ID — Idaho' },
      { value: 'IL', label: 'IL — Illinois' },
      { value: 'IN', label: 'IN — Indiana' },
      { value: 'IA', label: 'IA — Iowa' },
      { value: 'KS', label: 'KS — Kansas' },
      { value: 'KY', label: 'KY — Kentucky' },
      { value: 'LA', label: 'LA — Louisiana' },
      { value: 'ME', label: 'ME — Maine' },
      { value: 'MD', label: 'MD — Maryland' },
      { value: 'MA', label: 'MA — Massachusetts' },
      { value: 'MI', label: 'MI — Michigan' },
      { value: 'MN', label: 'MN — Minnesota' },
      { value: 'MS', label: 'MS — Mississippi' },
      { value: 'MO', label: 'MO — Missouri' },
      { value: 'MT', label: 'MT — Montana' },
      { value: 'NE', label: 'NE — Nebraska' },
      { value: 'NV', label: 'NV — Nevada' },
      { value: 'NH', label: 'NH — New Hampshire' },
      { value: 'NJ', label: 'NJ — New Jersey' },
      { value: 'NM', label: 'NM — New Mexico' },
      { value: 'NY', label: 'NY — New York' },
      { value: 'NC', label: 'NC — North Carolina' },
      { value: 'ND', label: 'ND — North Dakota' },
      { value: 'OH', label: 'OH — Ohio' },
      { value: 'OK', label: 'OK — Oklahoma' },
      { value: 'OR', label: 'OR — Oregon' },
      { value: 'PA', label: 'PA — Pennsylvania' },
      { value: 'RI', label: 'RI — Rhode Island' },
      { value: 'SC', label: 'SC — South Carolina' },
      { value: 'SD', label: 'SD — South Dakota' },
      { value: 'TN', label: 'TN — Tennessee' },
      { value: 'TX', label: 'TX — Texas' },
      { value: 'UT', label: 'UT — Utah' },
      { value: 'VT', label: 'VT — Vermont' },
      { value: 'VA', label: 'VA — Virginia' },
      { value: 'WA', label: 'WA — Washington' },
      { value: 'WV', label: 'WV — West Virginia' },
      { value: 'WI', label: 'WI — Wisconsin' },
      { value: 'WY', label: 'WY — Wyoming' },
    ],
  },
]

function field(definition: DocumentFieldDefinition) {
  return definition
}

export const CBP_7501_SECTIONS: DocumentSectionDefinition[] = [
  {
    id: 'entry_overview',
    title: 'Entry Overview',
    description: 'Entry summary header details for filing type, bond, port, and date control.',
    fields: [
      field({
        key: 'entry_number',
        blockNumber: '1',
        officialName: 'Filer Code / Entry Number',
        label: 'Block 1 — Filer Code / Entry Number',
        type: 'text',
        required: true,
        placeholder: 'ABC12345678',
        exampleValue: 'ABC12345678',
        helpText: 'Record the 11-character entry number using the three-character filer code, seven-digit entry number, and one-digit check digit.',
        aceMappingId: '7501.block_1.entry_number',
        validation: [
          { type: 'required', message: 'Entry number is required.' },
          {
            type: 'pattern',
            value: '^[A-Z0-9]{3}[0-9]{8}$',
            message: 'Use the filer code plus seven-digit entry number and check digit without spaces.',
          },
        ],
      }),
      field({
        key: 'entry_type',
        blockNumber: '2',
        officialName: 'Entry Type',
        label: 'Block 2 — Entry Type',
        type: 'select',
        required: true,
        aceMappingId: '7501.block_2.entry_type',
        enumSource: 'CBP Form 7501 instructions, block 2',
        helpText: 'Select the official two digit entry type code for the entry summary being filed.',
        options: CBP_7501_ENTRY_TYPE_OPTIONS,
        validation: [{ type: 'required', message: 'Entry type is required.' }],
      }),
      field({
        key: 'summary_date',
        blockNumber: '3',
        officialName: 'Summary Date',
        label: 'Block 3 — Summary Date',
        type: 'date',
        required: true,
        placeholder: 'MM/DD/YYYY',
        helpText: 'Record the month, day, and year the entry summary is filed with CBP.',
        aceMappingId: '7501.block_3.summary_date',
        validation: [{ type: 'required', message: 'Summary date is required.' }, DATE_RULE],
      }),
      field({
        key: 'surety_number',
        blockNumber: '4',
        officialName: 'Surety Number',
        label: 'Block 4 — Surety Number',
        type: 'text',
        required: true,
        placeholder: '123',
        helpText: 'Use the three digit surety code from the customs bond, or 998/999 where the instructions require it.',
        aceMappingId: '7501.block_4.surety_number',
        validation: [
          { type: 'required', message: 'Surety number is required.' },
          { type: 'pattern', value: '^\\d{3}$', message: 'Use the three digit surety code.' },
        ],
      }),
      field({
        key: 'bond_type',
        blockNumber: '5',
        officialName: 'Bond Type',
        label: 'Block 5 — Bond Type',
        type: 'select',
        required: true,
        aceMappingId: '7501.block_5.bond_type',
        enumSource: 'CBP Form 7501 instructions, block 5',
        helpText: 'Use 0, 8, or 9 based on the bond coverage described in the instructions.',
        options: CBP_7501_BOND_TYPE_OPTIONS,
        validation: [{ type: 'required', message: 'Bond type is required.' }],
      }),
      field({
        key: 'port_of_entry',
        blockNumber: '6',
        officialName: 'Port Code',
        label: 'Block 6 — Port Code',
        type: 'text',
        required: true,
        placeholder: '4911',
        helpText: 'Use the four digit U.S. port code in DDPP format with no spaces or hyphens.',
        aceMappingId: '7501.block_6.port_code',
        validation: [{ type: 'required', message: 'Port code is required.' }, PORT_RULE],
      }),
      field({
        key: 'entry_date',
        blockNumber: '7',
        officialName: 'Entry Date',
        label: 'Block 7 — Entry Date',
        type: 'date',
        required: true,
        placeholder: 'MM/DD/YYYY',
        helpText: 'Record the release or withdrawal date using MM/DD/YYYY.',
        aceMappingId: '7501.block_7.entry_date',
        validation: [{ type: 'required', message: 'Entry date is required.' }, DATE_RULE],
      }),
    ],
  },
  {
    id: 'shipment_transport',
    title: 'Shipment & Transport',
    description: 'Carrier, movement, import timing, and foreign/U.S. port data.',
    fields: [
      field({
        key: 'importing_carrier',
        blockNumber: '8',
        officialName: 'Importing Carrier',
        label: 'Block 8 — Importing Carrier',
        type: 'text',
        placeholder: 'Carrier name or IATA code',
        helpText: 'Use the vessel name for vessel shipments or the two character IATA code for air shipments.',
        aceMappingId: '7501.block_8.importing_carrier',
      }),
      field({
        key: 'mode_of_transport',
        blockNumber: '9',
        officialName: 'Mode of Transport',
        label: 'Block 9 — Mode of Transport',
        type: 'select',
        required: true,
        aceMappingId: '7501.block_9.mode_of_transport',
        enumSource: 'CBP Form 7501 instructions, block 9',
        helpText: 'Use the official two digit mode of transport code for the imported merchandise.',
        options: CBP_7501_MODE_OF_TRANSPORT_OPTIONS,
        validation: [{ type: 'required', message: 'Mode of transport is required.' }],
      }),
      field({
        key: 'country_of_origin',
        blockNumber: '10',
        officialName: 'Country of Origin',
        label: 'Block 10 — Country of Origin',
        type: 'select',
        required: true,
        placeholder: 'Select a Country',
        helpText: 'Use the ISO country code, or MULTI when the instructions allow it.',
        aceMappingId: '7501.block_10.country_of_origin',
        options: CBP_7501_COUNTRY_CODE_OPTIONS,
        validation: [{ type: 'required', message: 'Country of origin is required.' }, ORIGIN_EXPORT_COUNTRY_RULE],
      }),
      field({
        key: 'import_date',
        blockNumber: '11',
        officialName: 'Import Date',
        label: 'Block 11 — Import Date',
        type: 'date',
        required: true,
        placeholder: 'MM/DD/YYYY',
        helpText: 'Record the month, day, and year the merchandise arrived within the U.S. port limits.',
        aceMappingId: '7501.block_11.import_date',
        validation: [{ type: 'required', message: 'Import date is required.' }, DATE_RULE],
      }),
      field({
        key: 'bill_of_lading_awb_number',
        blockNumber: '12',
        officialName: 'B/L or AWB Number',
        label: 'Block 12 — B/L or AWB Number',
        type: 'text',
        required: true,
        placeholder: 'ABCD1234567',
        helpText: 'Record the number assigned on the manifest by the international carrier delivering the goods to the U.S.',
        aceMappingId: '7501.block_12.bill_of_lading_awb_number',
        validation: [
          { type: 'required', message: 'B/L or AWB number is required.' },
          { type: 'pattern', value: '^[A-Z0-9]{1,16}$', message: 'Use one to sixteen letters or numbers with no spaces.' },
        ],
      }),
      field({
        key: 'foreign_port_of_lading',
        blockNumber: '19',
        officialName: 'Foreign Port of Lading',
        label: 'Block 19 — Foreign Port of Lading',
        type: 'text',
        placeholder: '12345',
        helpText: 'Use the five digit Schedule K code when the merchandise arrived by vessel.',
        aceMappingId: '7501.block_19.foreign_port_of_lading',
        validation: [{ type: 'pattern', value: '^\\d{5}$', message: 'Use the five digit Schedule K code.' }],
      }),
      field({
        key: 'us_port_of_unlading',
        blockNumber: '20',
        officialName: 'U.S. Port of Unlading',
        label: 'Block 20 — U.S. Port of Unlading',
        type: 'text',
        placeholder: '4909',
        helpText: 'Use the U.S. port code where the merchandise was unladen from the importing vessel, aircraft, or train.',
        aceMappingId: '7501.block_20.us_port_of_unlading',
        validation: [PORT_RULE],
      }),
      field({
        key: 'location_of_goods_go_number',
        blockNumber: '25',
        officialName: 'Location of Goods / G.O. Number',
        label: 'Block 25 — Location of Goods / G.O. Number',
        type: 'text',
        placeholder: 'FIRMS code, flight number, or G.O. number',
        helpText: 'Record the FIRMS code, flight number, or G.O. number as required by the filing scenario.',
        aceMappingId: '7501.block_25.location_of_goods_go_number',
      }),
    ],
  },
  {
    id: 'origin_export_trade_controls',
    title: 'Origin / Export / Trade Controls',
    description: 'Country, manufacturer, in-bond, missing-doc, and Section 232 trade-control fields.',
    fields: [
      field({
        key: 'manufacturer_id',
        blockNumber: '13',
        officialName: 'Manufacturer ID',
        label: 'Block 13 — Manufacturer ID',
        type: 'text',
        required: true,
        placeholder: 'Constructed MID',
        helpText: 'Use the constructed MID for the invoicing party or actual manufacturer as required by the instructions.',
        aceMappingId: '7501.block_13.manufacturer_id',
        validation: [
          { type: 'required', message: 'Manufacturer ID is required.' },
          { type: 'pattern', value: '^(MULTI|[A-Z0-9]{2,15})$', message: 'Use the constructed MID format or MULTI.' },
        ],
      }),
      field({
        key: 'exporting_country',
        blockNumber: '14',
        officialName: 'Exporting Country',
        label: 'Block 14 — Exporting Country',
        type: 'select',
        required: true,
        placeholder: 'Select a Country',
        helpText: 'Use the ISO country code for the country from which the merchandise was shipped to the U.S.',
        aceMappingId: '7501.block_14.exporting_country',
        options: CBP_7501_COUNTRY_CODE_OPTIONS,
        validation: [{ type: 'required', message: 'Exporting country is required.' }, ORIGIN_EXPORT_COUNTRY_RULE],
      }),
      field({
        key: 'export_date',
        blockNumber: '15',
        officialName: 'Export Date',
        label: 'Block 15 — Export Date',
        type: 'date',
        placeholder: 'MM/DD/YYYY',
        helpText: 'Record the departure or border crossing date from the exporting country.',
        aceMappingId: '7501.block_15.export_date',
        validation: [DATE_RULE],
      }),
      field({
        key: 'it_number',
        blockNumber: '16',
        officialName: 'I.T. Number',
        label: 'Block 16 — I.T. Number',
        type: 'text',
        conditional: true,
        visibleWhen: [{ fieldKey: 'it_date', operator: 'not_empty' }],
        helpText: 'Record the in-bond or immediate transportation number when in-bond movement applies.',
        aceMappingId: '7501.block_16.it_number',
      }),
      field({
        key: 'it_date',
        blockNumber: '17',
        officialName: 'I.T. Date',
        label: 'Block 17 — I.T. Date',
        type: 'date',
        conditional: true,
        visibleWhen: [{ fieldKey: 'it_number', operator: 'not_empty' }],
        helpText: 'Record the IT date from the CBP Form 7512, TACM, or AMS MIB record. It cannot be earlier than the import date.',
        aceMappingId: '7501.block_17.it_date',
        validation: [DATE_RULE],
      }),
      field({
        key: 'missing_docs',
        blockNumber: '18',
        officialName: 'Missing Docs',
        label: 'Block 18 — Missing Docs',
        type: 'text',
        conditional: true,
        placeholder: '01,99',
        exampleValue: '01,99',
        helpText: 'Record up to two missing document codes. Use 99 only when three or more documents are missing.',
        aceMappingId: '7501.block_18.missing_docs',
        enumSource: 'CBP Form 7501 instructions, block 18',
        validation: [
          {
            type: 'custom',
            message: 'Use up to two valid missing-document codes, and pair 99 only with one other code.',
          },
        ],
      }),
      field({
        key: 'country_of_melt_and_pour',
        blockNumber: '21',
        officialName: 'Country of Melt and Pour',
        label: 'Block 21 — Country of Melt and Pour',
        type: 'text',
        conditional: true,
        visibleWhen: [{ fieldKey: 'section_232_steel_indicator', operator: 'equals', value: 'Y' }],
        placeholder: 'TR',
        helpText: 'Required for articles of steel subject to Section 232, and certain derivative steel articles from Mexico.',
        aceMappingId: '7501.block_21.country_of_melt_and_pour',
        validation: [METAL_COUNTRY_RULE],
      }),
      field({
        key: 'primary_country_of_smelt',
        blockNumber: '22',
        officialName: 'Primary Country of Smelt',
        label: 'Block 22 — Primary Country of Smelt',
        type: 'text',
        conditional: true,
        visibleWhen: [{ fieldKey: 'section_232_aluminum_indicator', operator: 'equals', value: 'Y' }],
        placeholder: 'AE',
        helpText: 'Required for aluminum articles subject to the Section 232 aluminum remedies.',
        aceMappingId: '7501.block_22.primary_country_of_smelt',
        validation: [METAL_COUNTRY_RULE],
      }),
      field({
        key: 'secondary_country_of_smelt',
        blockNumber: '23',
        officialName: 'Secondary Country of Smelt',
        label: 'Block 23 — Secondary Country of Smelt',
        type: 'text',
        conditional: true,
        visibleWhen: [{ fieldKey: 'section_232_aluminum_indicator', operator: 'equals', value: 'Y' }],
        placeholder: 'CA or N/A',
        helpText: 'Use the ISO code for the second largest volume of new aluminum metal, or the special handling described for recycled aluminum.',
        aceMappingId: '7501.block_23.secondary_country_of_smelt',
        validation: [METAL_COUNTRY_RULE],
      }),
      field({
        key: 'country_of_cast',
        blockNumber: '24',
        officialName: 'Country of Cast',
        label: 'Block 24 — Country of Cast',
        type: 'text',
        conditional: true,
        visibleWhen: [{ fieldKey: 'section_232_aluminum_indicator', operator: 'equals', value: 'Y' }],
        placeholder: 'MX',
        helpText: 'Use the ISO code where the aluminum was last liquified by heat and cast into solid state.',
        aceMappingId: '7501.block_24.country_of_cast',
        validation: [METAL_COUNTRY_RULE],
      }),
      field({
        key: 'section_232_steel_indicator',
        label: 'Section 232 steel article applies',
        type: 'select',
        conditional: true,
        options: YES_NO_OPTIONS,
        helpText: 'Turn this on when Block 21 is required for the entry.',
        aceMappingId: '7501.context.section_232_steel_indicator',
      }),
      field({
        key: 'section_232_aluminum_indicator',
        label: 'Section 232 aluminum article applies',
        type: 'select',
        conditional: true,
        options: YES_NO_OPTIONS,
        helpText: 'Turn this on when Blocks 22, 23, and 24 are required for the entry.',
        aceMappingId: '7501.context.section_232_aluminum_indicator',
      }),
    ],
  },
  {
    id: 'parties_identification',
    title: 'Parties & Identification Numbers',
    description: 'Consignee, importer, reference, and address details for the entry summary.',
    fields: [
      field({
        key: 'consignee_number',
        blockNumber: '26',
        officialName: 'Consignee Number',
        label: 'Block 26 — Consignee Number',
        type: 'text',
        required: true,
        placeholder: '12-3456789',
        helpText: 'Use EIN, SSN, CBP assigned number, SAME, or 00-0000000 for consolidated shipments.',
        aceMappingId: '7501.block_26.consignee_number',
        validation: [{ type: 'required', message: 'Consignee number is required.' }, IDENTIFIER_RULE],
      }),
      field({
        key: 'importer_number',
        blockNumber: '27',
        officialName: 'Importer Number',
        label: 'Block 27 — Importer Number',
        type: 'text',
        required: true,
        placeholder: '12-3456789',
        helpText: 'Use the importer EIN, SSN, or CBP assigned number in the same approved formats as Block 26.',
        aceMappingId: '7501.block_27.importer_number',
        validation: [{ type: 'required', message: 'Importer number is required.' }, IDENTIFIER_RULE],
      }),
      field({
        key: 'reference_number',
        blockNumber: '28',
        officialName: 'Reference Number',
        label: 'Block 28 — Reference Number',
        type: 'text',
        conditional: true,
        placeholder: '12-3456789',
        helpText: 'Use this only when refunds, bills, or notices go to a party other than the importer of record and a CBP Form 4811 is on file.',
        aceMappingId: '7501.block_28.reference_number',
        validation: [IDENTIFIER_RULE],
      }),
      field({
        key: 'ultimate_consignee_name',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — Ultimate Consignee Name',
        type: 'text',
        placeholder: 'Last, First, M.I. or business name',
        helpText: 'Record the purchaser, consignee, or destination party as described in the instructions.',
        aceMappingId: '7501.block_29.ultimate_consignee_name',
      }),
      field({
        key: 'ultimate_consignee_country',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — Country',
        type: 'select',
        placeholder: 'Select a Country',
        helpText: 'Record the destination country for the ultimate consignee address.',
        aceMappingId: '7501.block_29.ultimate_consignee_country',
        options: CBP_7501_ADDRESS_COUNTRY_OPTIONS,
      }),
      field({
        key: 'ultimate_consignee_address',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — Address Line 1',
        type: 'text',
        placeholder: 'Street address',
        helpText: 'Record the first line of the ultimate consignee street address.',
        aceMappingId: '7501.block_29.ultimate_consignee_address_line_1',
      }),
      field({
        key: 'ultimate_consignee_address_line_2',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — Address Line 2',
        type: 'text',
        placeholder: 'Apartment, suite, or unit',
        helpText: 'Use this only when a second address line is needed.',
        aceMappingId: '7501.block_29.ultimate_consignee_address_line_2',
      }),
      field({
        key: 'ultimate_consignee_city_location_estate',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — Estate / Location',
        type: 'text',
        placeholder: 'Estate / Location (Optional)',
        helpText: 'Record the estate or location for the ultimate consignee address.',
        aceMappingId: '7501.block_29.ultimate_consignee_estate_location',
      }),
      field({
        key: 'ultimate_consignee_city_island',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — City / Island',
        type: 'text',
        placeholder: 'City / Island',
        helpText: 'Record the city or island for the ultimate consignee address.',
        aceMappingId: '7501.block_29.ultimate_consignee_city_island',
      }),
      field({
        key: 'ultimate_consignee_state_island',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — State / Island',
        type: 'select',
        placeholder: 'Select state or island',
        helpText: 'Select the state, territory, or island used for the ultimate consignee address.',
        aceMappingId: '7501.block_29.ultimate_consignee_state_island',
        options: CBP_7501_STATE_ISLAND_OPTIONS,
      }),
      field({
        key: 'ultimate_consignee_zip_code',
        blockNumber: '29',
        officialName: 'Ultimate Consignee Name and Address',
        label: 'Block 29 — ZIP Code',
        type: 'text',
        placeholder: 'Zip Code',
        helpText: 'Record the ZIP code for the ultimate consignee address.',
        aceMappingId: '7501.block_29.ultimate_consignee_zip_code',
        validation: [ZIP_CODE_RULE],
      }),
      field({
        key: 'importer_of_record',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — Importer of Record Name',
        type: 'text',
        required: true,
        placeholder: 'Last, First, M.I. or business name',
        helpText: 'Record the owner, purchaser, or licensed broker serving as the importer of record.',
        aceMappingId: '7501.block_30.importer_of_record_name',
        validation: [{ type: 'required', message: 'Importer of record name is required.' }],
      }),
      field({
        key: 'importer_of_record_country',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — Country',
        type: 'select',
        required: true,
        placeholder: 'Select a Country',
        helpText: 'Record the country for the importer of record address.',
        aceMappingId: '7501.block_30.importer_of_record_country',
        options: CBP_7501_ADDRESS_COUNTRY_OPTIONS,
        validation: [{ type: 'required', message: 'Importer of record country is required.' }],
      }),
      field({
        key: 'importer_of_record_address',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — Address Line 1',
        type: 'text',
        required: true,
        placeholder: 'Street address',
        helpText: 'Record the first line of the importer of record street address.',
        aceMappingId: '7501.block_30.importer_of_record_address_line_1',
        validation: [{ type: 'required', message: 'Importer of record address line 1 is required.' }],
      }),
      field({
        key: 'importer_of_record_address_line_2',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — Address Line 2',
        type: 'text',
        placeholder: 'Apartment, suite, or unit',
        helpText: 'Use this only when a second address line is needed.',
        aceMappingId: '7501.block_30.importer_of_record_address_line_2',
      }),
      field({
        key: 'importer_of_record_city_location_estate',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — Estate / Location',
        type: 'text',
        required: true,
        placeholder: 'Estate / Location (Optional)',
        helpText: 'Record the estate or location for the importer of record address.',
        aceMappingId: '7501.block_30.importer_of_record_estate_location',
        validation: [{ type: 'required', message: 'Importer of record estate or location is required.' }],
      }),
      field({
        key: 'importer_of_record_city_island',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — City / Island',
        type: 'text',
        required: true,
        placeholder: 'City / Island',
        helpText: 'Record the city or island for the importer of record address.',
        aceMappingId: '7501.block_30.importer_of_record_city_island',
        validation: [{ type: 'required', message: 'Importer of record city or island is required.' }],
      }),
      field({
        key: 'importer_of_record_state_island',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — State / Island',
        type: 'select',
        required: true,
        placeholder: 'Select state or island',
        helpText: 'Select the state, territory, or island used for the importer of record address.',
        aceMappingId: '7501.block_30.importer_of_record_state_island',
        options: CBP_7501_STATE_ISLAND_OPTIONS,
        validation: [{ type: 'required', message: 'Importer of record state or island is required.' }],
      }),
      field({
        key: 'importer_of_record_zip_code',
        blockNumber: '30',
        officialName: 'Importer of Record Name and Address',
        label: 'Block 30 — ZIP Code',
        type: 'text',
        required: true,
        placeholder: 'Zip Code',
        helpText: 'Record the ZIP code for the importer of record address.',
        aceMappingId: '7501.block_30.importer_of_record_zip_code',
        validation: [
          { type: 'required', message: 'Importer of record ZIP code is required.' },
          ZIP_CODE_RULE,
        ],
      }),
    ],
  },
  {
    id: 'line_items',
    title: 'Line Items',
    description: 'Continuation-sheet style line items for merchandise, classification, values, and duty/tax details.',
    kind: 'line_items',
    fields: [
      field({
        key: 'line_items_registry',
        label: 'Line item registry',
        type: 'text',
        editable: false,
        helpText: 'Rendered through the dedicated repeating line-item editor.',
        aceMappingId: '7501.line_items',
      }),
    ],
  },
  {
    id: 'fees_duties_totals',
    title: 'Fees, Duties, and Totals',
    description: 'Summary fees and totals with reconciliation against the current line item data.',
    fields: [
      field({
        key: 'other_fee_summary',
        officialName: 'Other Fee Summary',
        label: 'Other Fee Summary',
        type: 'textarea',
        placeholder: 'Collection code and amount details',
        helpText: 'List the applicable collection codes and summary fee amounts shown with the total entered value section.',
        aceMappingId: '7501.other_fee_summary',
      }),
      field({
        key: 'total_entered_value',
        blockNumber: '39',
        officialName: 'Total Entered Value',
        label: 'Block 39 — Total Entered Value',
        type: 'currency',
        required: true,
        placeholder: '0.00',
        aceMappingId: '7501.block_39.total_entered_value',
        validation: [{ type: 'required', message: 'Total entered value is required.' }, CURRENCY_RULE],
      }),
      field({
        key: 'total_other_fees',
        officialName: 'Total Other Fees',
        label: 'Total Other Fees',
        type: 'currency',
        placeholder: '0.00',
        aceMappingId: '7501.total_other_fees',
        validation: [CURRENCY_RULE],
      }),
      field({
        key: 'duty_total',
        blockNumber: '41',
        officialName: 'Duty',
        label: 'Block 41 — Duty',
        type: 'currency',
        placeholder: '0.00',
        aceMappingId: '7501.block_41.duty',
        validation: [CURRENCY_RULE],
      }),
      field({
        key: 'tax_total',
        blockNumber: '42',
        officialName: 'Tax',
        label: 'Block 42 — Tax',
        type: 'currency',
        placeholder: '0.00',
        aceMappingId: '7501.block_42.tax',
        validation: [CURRENCY_RULE],
      }),
      field({
        key: 'other_total',
        blockNumber: '43',
        officialName: 'Other',
        label: 'Block 43 — Other',
        type: 'currency',
        placeholder: '0.00',
        aceMappingId: '7501.block_43.other',
        validation: [CURRENCY_RULE],
      }),
      field({
        key: 'grand_total',
        blockNumber: '44',
        officialName: 'Total',
        label: 'Block 44 — Total',
        type: 'currency',
        placeholder: '0.00',
        aceMappingId: '7501.block_44.total',
        validation: [CURRENCY_RULE],
      }),
      field({
        key: 'totals_override_reason',
        label: 'Totals override reason',
        type: 'textarea',
        conditional: true,
        helpText: 'Explain any manual override when totals differ from the calculated line-item totals.',
        aceMappingId: '7501.totals_override_reason',
      }),
    ],
  },
  {
    id: 'declaration_filing_metadata',
    title: 'Declaration & Filing Metadata',
    description: 'Declaration, signer, and broker/filer metadata required to complete the form.',
    fields: [
      field({
        key: 'declaration_role',
        blockNumber: '40',
        officialName: 'Declaration of Importer of Record or Authorized Agent',
        label: 'Block 40 — Declaration Role',
        type: 'select',
        required: true,
        placeholder: 'Select a declaration role',
        helpText: 'Select whether the declarant is the importer of record or an authorized agent.',
        aceMappingId: '7501.block_40.declaration_role',
        options: [
          { value: 'importer_of_record', label: 'Importer of record' },
          { value: 'owner', label: 'Owner' },
          { value: 'purchaser', label: 'Purchaser' },
          { value: 'authorized_agent', label: 'Authorized agent' },
        ],
        validation: [{ type: 'required', message: 'Declaration role is required.' }],
      }),
      field({
        key: 'declaration_basis',
        blockNumber: '40',
        officialName: 'Declaration of Importer of Record or Authorized Agent',
        label: 'Block 40 — Declaration Basis',
        type: 'select',
        helpText: 'Indicate whether the merchandise was obtained pursuant to a purchase or agreement to purchase.',
        aceMappingId: '7501.block_40.declaration_basis',
        options: [
          { value: 'purchase', label: 'Purchase or agreement to purchase' },
          { value: 'not_purchase', label: 'Not obtained by purchase' },
        ],
      }),
      field({
        key: 'declarant_name',
        blockNumber: '45',
        officialName: 'Declarant Name',
        label: 'Block 45 — Declarant Name',
        type: 'text',
        required: true,
        placeholder: 'Last, First, M.I.',
        helpText: 'Record the name of the owner, purchaser, or agent signing the declaration.',
        aceMappingId: '7501.block_45.declarant_name',
        validation: [{ type: 'required', message: 'Declarant name is required.' }],
      }),
      field({
        key: 'declarant_title',
        blockNumber: '45',
        officialName: 'Declarant Title',
        label: 'Block 45 — Declarant Title',
        type: 'text',
        placeholder: 'Import specialist',
        helpText: 'Record the job title of the declarant signing the form.',
        aceMappingId: '7501.block_45.declarant_title',
      }),
      field({
        key: 'signature',
        blockNumber: '45',
        officialName: 'Signature',
        label: 'Block 45 — Signature',
        type: 'text',
        placeholder: 'Electronic certification or typed signature',
        helpText: 'For ABI, certification is the electronic equivalent of a signature.',
        aceMappingId: '7501.block_45.signature',
      }),
      field({
        key: 'signature_date',
        blockNumber: '45',
        officialName: 'Date',
        label: 'Block 45 — Signature Date',
        type: 'date',
        placeholder: 'MM/DD/YYYY',
        aceMappingId: '7501.block_45.date',
        validation: [DATE_RULE],
      }),
      field({
        key: 'broker_filer_information',
        blockNumber: '46',
        officialName: 'Broker/Filer Information Name and Phone Number',
        label: 'Block 46 — Broker/Filer Information',
        type: 'textarea',
        placeholder: 'Name and phone number',
        helpText: 'This block is reserved for broker or filer information, including phone number.',
        aceMappingId: '7501.block_46.broker_filer_information',
      }),
      field({
        key: 'broker_importer_file_number',
        blockNumber: '47',
        officialName: 'Broker/Importer File Number',
        label: 'Block 47 — Broker/Importer File Number',
        type: 'text',
        placeholder: 'Internal file number',
        aceMappingId: '7501.block_47.broker_importer_file_number',
      }),
    ],
  },
]

export const CBP_7501_LINE_ITEM_FIELDS: DocumentFieldDefinition[] = [
  field({
    key: 'line_number',
    columnNumber: '31',
    officialName: 'Line Number',
    label: 'Column 31 — Line Number',
    type: 'text',
    required: true,
    placeholder: '001',
    helpText: 'Record line numbers in sequence beginning with 001.',
    aceMappingId: '7501.line_31.line_number',
    validation: [
      { type: 'required', message: 'Line number is required.' },
      { type: 'pattern', value: '^\\d{3}$', message: 'Use a three digit line number such as 001.' },
    ],
  }),
  field({
    key: 'description_of_merchandise',
    columnNumber: '32',
    officialName: 'Description of Merchandise',
    label: 'Column 32 — Description of Merchandise',
    type: 'textarea',
    required: true,
    placeholder: 'Detailed merchandise description',
    helpText: 'Provide enough detail to permit classification under the proper statistical reporting number.',
    aceMappingId: '7501.line_32.description_of_merchandise',
    validation: [{ type: 'required', message: 'Merchandise description is required.' }],
  }),
  field({
    key: 'htsus_number',
    columnNumber: '33A',
    officialName: 'HTSUS Number',
    label: 'Column 33A — HTSUS Number',
    type: 'text',
    required: true,
    placeholder: '4012.11.4000',
    helpText: 'Record the full 10 digit HTS item number with decimals exactly as shown in the HTS.',
    aceMappingId: '7501.line_33A.htsus_number',
    validation: [
      { type: 'required', message: 'HTSUS number is required.' },
      { type: 'pattern', value: '^\\d{4}\\.\\d{2}\\.\\d{4}$', message: 'Use the 10 digit HTSUS format with decimals.' },
    ],
  }),
  field({
    key: 'ad_cvd_number',
    columnNumber: '33B',
    officialName: 'AD/CVD Number',
    label: 'Column 33B — AD/CVD Number',
    type: 'text',
    conditional: true,
    placeholder: 'A-000-000-000',
    helpText: 'Record the AD/CVD case number only when applicable.',
    aceMappingId: '7501.line_33B.ad_cvd_number',
    validation: [
      { type: 'pattern', value: '^[AC]-?\\d{3}-\\d{3}-\\d{3}$', message: 'Use the AD/CVD case number format shown in the instructions.' },
    ],
  }),
  field({
    key: 'gross_weight',
    columnNumber: '34A',
    officialName: 'Gross Weight',
    label: 'Column 34A — Gross Weight',
    type: 'number',
    required: true,
    placeholder: '50',
    helpText: 'Report gross shipping weight in kilograms for all modes of transportation. This field is always recorded in KG and is not derived from the HTSUS code.',
    aceMappingId: '7501.line_34A.gross_weight',
    validation: [{ type: 'required', message: 'Gross weight is required.' }, NUMERIC_RULE],
  }),
  field({
    key: 'gross_weight_unit',
    columnNumber: '34A',
    officialName: 'Gross Weight Unit',
    label: 'Column 34A — Gross Weight Unit',
    type: 'select',
    required: true,
    placeholder: 'KG',
    helpText: 'Gross Weight is always reported in kilograms (KG) for this workflow. It is fixed to KG and does not change based on the HTSUS code.',
    aceMappingId: '7501.line_34A.gross_weight_unit',
    enumSource: 'KG only',
    options: CBP_7501_WEIGHT_UNIT_OPTIONS,
    validation: [{ type: 'required', message: 'Gross weight unit is required.' }],
  }),
  field({
    key: 'manifest_quantity',
    columnNumber: '34B',
    officialName: 'Manifest Quantity',
    label: 'Column 34B — Manifest Quantity',
    type: 'number',
    placeholder: '12',
    helpText: 'Record the manifest quantity reported on the bill of lading or air waybill.',
    aceMappingId: '7501.line_34B.manifest_quantity',
    validation: [NUMERIC_RULE],
  }),
  field({
    key: 'manifest_quantity_unit',
    columnNumber: '34B',
    officialName: 'Manifest Quantity Unit',
    label: 'Column 34B — Manifest Quantity Unit',
    type: 'select',
    placeholder: 'Select unit',
    helpText: 'Choose the unit of measure or package type used for the manifest quantity.',
    aceMappingId: '7501.line_34B.manifest_quantity_unit',
    enumSource: 'ea, pcs, pkg, ctn, pal, doz',
    options: CBP_7501_MANIFEST_QUANTITY_UNIT_OPTIONS,
  }),
  field({
    key: 'net_quantity_htsus_units',
    columnNumber: '35',
    officialName: 'Net Quantity in HTSUS Units',
    label: 'Column 35 — Net Quantity in HTSUS Units',
    type: 'text',
    required: true,
    placeholder: '50 doz.',
    helpText: 'Report the net quantity using the HTS-required unit of measure.',
    aceMappingId: '7501.line_35.net_quantity_htsus_units',
    validation: [{ type: 'required', message: 'Net quantity in HTSUS units is required.' }],
  }),
  field({
    key: 'entered_value',
    columnNumber: '36A',
    officialName: 'Entered Value',
    label: 'Column 36A — Entered Value',
    type: 'currency',
    required: true,
    placeholder: '0.00',
    helpText: 'Record the U.S. dollar entered value for the line item.',
    aceMappingId: '7501.line_36A.entered_value',
    validation: [{ type: 'required', message: 'Entered value is required.' }, CURRENCY_RULE],
  }),
  field({
    key: 'charges',
    columnNumber: '36B',
    officialName: 'CHGS',
    label: 'Column 36B — CHGS',
    type: 'currency',
    placeholder: '0.00',
    helpText: 'Record applicable charges for the line item.',
    aceMappingId: '7501.line_36B.charges',
    validation: [CURRENCY_RULE],
  }),
  field({
    key: 'relationship',
    columnNumber: '36C',
    officialName: 'Relationship',
    label: 'Column 36C — Relationship',
    type: 'select',
    required: true,
    placeholder: 'Select relationship',
    helpText: 'Indicate whether the buyer and seller are related.',
    aceMappingId: '7501.line_36C.relationship',
    options: YES_NO_OPTIONS,
    validation: [{ type: 'required', message: 'Relationship is required.' }],
  }),
  field({
    key: 'htsus_rate',
    columnNumber: '37A',
    officialName: 'HTSUS Rate',
    label: 'Column 37A — HTSUS Rate',
    type: 'text',
    placeholder: '5.7%',
    helpText: 'Record the HTSUS rate used for duty calculation.',
    aceMappingId: '7501.line_37A.htsus_rate',
  }),
  field({
    key: 'ad_cvd_rate',
    columnNumber: '37B',
    officialName: 'AD/CVD Rate',
    label: 'Column 37B — AD/CVD Rate',
    type: 'text',
    conditional: true,
    placeholder: '12.50%',
    helpText: 'Record the AD/CVD rate only when applicable.',
    aceMappingId: '7501.line_37B.ad_cvd_rate',
  }),
  field({
    key: 'irc_rate',
    columnNumber: '37C',
    officialName: 'IRC Rate',
    label: 'Column 37C — IRC Rate',
    type: 'text',
    conditional: true,
    placeholder: '0.00%',
    helpText: 'Record the internal revenue code rate when applicable.',
    aceMappingId: '7501.line_37C.irc_rate',
  }),
  field({
    key: 'visa_certificate_number',
    columnNumber: '37D',
    officialName: 'Visa Number / Certificate / Export Certificate / Agriculture License',
    label: 'Column 37D — Visa / Certificate / Export Certificate / Agriculture License',
    type: 'text',
    conditional: true,
    placeholder: 'Certificate or license number',
    helpText: 'Use this field only when the commodity or program requires visa, certificate, export certificate, or agriculture license reporting.',
    aceMappingId: '7501.line_37D.visa_certificate_number',
    validation: [{ type: 'pattern', value: '^[VC][0-9][A-Z]{2}[A-Z0-9]{6}$', message: 'Use the visa or certificate number format required by the program.' }],
  }),
  field({
    key: 'duty_and_ir_tax',
    columnNumber: '38',
    officialName: 'Duty and IR Tax',
    label: 'Column 38 — Duty and IR Tax',
    type: 'currency',
    required: true,
    placeholder: '0.00',
    helpText: 'Record the estimated duty, AD/CVD, IR tax, and other charges calculated for the line item.',
    aceMappingId: '7501.line_38.duty_and_ir_tax',
    validation: [{ type: 'required', message: 'Duty and IR tax is required.' }, CURRENCY_RULE],
  }),
]

export const CBP_7501_FIELD_REGISTRY = [
  ...CBP_7501_SECTIONS.flatMap((section) =>
    section.kind === 'line_items'
      ? CBP_7501_LINE_ITEM_FIELDS.map((fieldDefinition) => ({
          ...fieldDefinition,
          sectionId: section.id,
          sectionTitle: section.title,
        }))
      : section.fields.map((fieldDefinition) => ({
          ...fieldDefinition,
          sectionId: section.id,
          sectionTitle: section.title,
        })),
  ),
]
